import { FC, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'src/components';
import { suppressExploreDifferentVocabModal } from './exploreDifferentVocabModalStorage';
import { useTrackTooEasyForYou } from './useTrackTooEasyForYou';
import * as S from './styles';

const msgs = defineMessages({
  title: {
    id: 'learn_session_known_words_vocab_modal_title',
    defaultMessage: 'Is this too easy for you?',
  },
  description: {
    id: 'learn_session_known_words_vocab_modal_description',
    defaultMessage:
      'If the level or topic is not quite right, you can switch to a different vocab list.',
  },
  dontShowAgain: {
    id: 'learn_session_known_words_vocab_modal_dont_show_again',
    defaultMessage: "Don't show again",
  },
  remain: {
    id: 'learn_session_known_words_vocab_modal_remain',
    defaultMessage: 'No, thanks',
  },
  explore: {
    id: 'learn_session_known_words_vocab_modal_explore',
    defaultMessage: 'Explore more word lists',
  },
});

export const ExploreDifferentVocabModal: FC<{
  isOpen: boolean;
  languagePairId: number;
  onRemain: () => void;
  onExplore: () => void;
}> = ({ isOpen, languagePairId, onRemain, onExplore }) => {
  const intl = useIntl();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const trackTooEasyForYou = useTrackTooEasyForYou(languagePairId);

  useEffect(() => {
    if (isOpen) {
      setDontShowAgain(false);
    }
  }, [isOpen]);

  const persistSuppressionIfNeeded = () => {
    if (dontShowAgain) {
      suppressExploreDifferentVocabModal();
    }
  };

  const handleRemain = () => {
    persistSuppressionIfNeeded();
    trackTooEasyForYou('too_easy_for_you_dismissed');
    onRemain();
  };

  const handleExplore = () => {
    persistSuppressionIfNeeded();
    trackTooEasyForYou('too_easy_for_you_explore_more');
    onExplore();
  };

  const handleDontShowAgainChange = () => {
    const nextValue = !dontShowAgain;
    setDontShowAgain(nextValue);
    if (nextValue) {
      trackTooEasyForYou('too_easy_for_you_dont_show_again');
    }
  };

  return (
    <S.WideModal
      isOpen={isOpen}
      handleClose={handleRemain}
      shouldCloseOnEsc
      darkCloseIcon
      data-testid="explore-different-vocab-modal"
    >
      <S.ModalContent>
        <S.Header>
          <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
        </S.Header>
        <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
        <S.DontShowAgain>
          <S.SmallCheckbox
            checked={dontShowAgain}
            onChange={handleDontShowAgainChange}
            aria-label={intl.formatMessage(msgs.dontShowAgain)}
          />
          <S.DontShowAgainText>{intl.formatMessage(msgs.dontShowAgain)}</S.DontShowAgainText>
        </S.DontShowAgain>
        <S.Actions>
          <Button
            buttonType="primary"
            buttonSize="medium"
            wide
            onClick={handleExplore}
            data-testid="explore-different-vocab-explore"
          >
            {intl.formatMessage(msgs.explore)}
          </Button>
          <S.RemainLink
            type="button"
            onClick={handleRemain}
            data-testid="explore-different-vocab-remain"
          >
            {intl.formatMessage(msgs.remain)}
          </S.RemainLink>
        </S.Actions>
      </S.ModalContent>
    </S.WideModal>
  );
};
